// Small helpers to render weekly business hours consistently across the app.
// Storage shape (server-side):
//   {
//     mon: { open: "09:00", close: "18:00", closed: false, by_appointment: false },
//     tue: { ..., closed: true },
//     ...
//   }
// A missing day is treated the same as `{ closed: true }`.

export const WEEKDAYS = [
  { key: "mon", label: "Mon" },
  { key: "tue", label: "Tue" },
  { key: "wed", label: "Wed" },
  { key: "thu", label: "Thu" },
  { key: "fri", label: "Fri" },
  { key: "sat", label: "Sat" },
  { key: "sun", label: "Sun" },
];

const JS_DAY_TO_KEY = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"];

export function emptyWeek() {
  return WEEKDAYS.reduce((acc, { key }) => {
    acc[key] = { open: "", close: "", closed: true, by_appointment: false };
    return acc;
  }, {});
}

// Merge a partial dict with an empty week so downstream code can trust
// every weekday key exists (and dead-simple day pickers just work).
export function normaliseWeek(hours) {
  const base = emptyWeek();
  if (!hours || typeof hours !== "object") return base;
  for (const { key } of WEEKDAYS) {
    const v = hours[key];
    if (v && typeof v === "object") {
      base[key] = {
        open: typeof v.open === "string" ? v.open : "",
        close: typeof v.close === "string" ? v.close : "",
        closed: !!v.closed,
        by_appointment: !!v.by_appointment,
      };
    }
  }
  return base;
}

// "09:00" → "9am", "18:30" → "6:30pm"
export function fmtTime12(hhmm) {
  if (!hhmm || typeof hhmm !== "string" || !hhmm.includes(":")) return "";
  const [hStr, mStr] = hhmm.split(":");
  const h = parseInt(hStr, 10);
  const m = parseInt(mStr, 10);
  if (Number.isNaN(h) || Number.isNaN(m)) return "";
  const suffix = h < 12 ? "am" : "pm";
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return m === 0 ? `${h12}${suffix}` : `${h12}:${String(m).padStart(2, "0")}${suffix}`;
}

// Returns { label, tone } for today's status.
//   tone: "open" | "closed" | "appt" | "unknown"
export function todayStatus(hours, now = new Date()) {
  const week = normaliseWeek(hours);
  const dayKey = JS_DAY_TO_KEY[now.getDay()];
  const day = week[dayKey];
  // No schedule at all → don't show anything on the card.
  const hasAnyDaySet = Object.values(week).some(
    (d) => !d.closed || d.by_appointment,
  );
  if (!hasAnyDaySet) return { label: "", tone: "unknown" };

  if (day.by_appointment) return { label: "By appointment", tone: "appt" };
  if (day.closed) return { label: "Closed today", tone: "closed" };
  if (!day.open || !day.close) return { label: "Closed today", tone: "closed" };

  // Compare current time-of-day (in the user's local tz — good enough:
  // consumers browsing Book Away are in the same tz as the business
  // 99% of the time; nothing here is scheduling across zones).
  const [oH, oM] = day.open.split(":").map(Number);
  const [cH, cM] = day.close.split(":").map(Number);
  const nowMin = now.getHours() * 60 + now.getMinutes();
  const openMin = oH * 60 + oM;
  const closeMin = cH * 60 + cM;
  if (nowMin < openMin) {
    return { label: `Opens ${fmtTime12(day.open)}`, tone: "closed" };
  }
  if (nowMin >= closeMin) {
    return { label: "Closed today", tone: "closed" };
  }
  return { label: `Open until ${fmtTime12(day.close)}`, tone: "open" };
}
